import React, { useState, useEffect, useContext, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  Chip,
  Divider,
  Select,
  MenuItem,
  Button,
  Badge,
  makeStyles,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import AddIcon from "@material-ui/icons/Add";
import SaveIcon from "@material-ui/icons/Save";
import { ThreeDots } from "react-loader-spinner";
import { toast } from "react-toastify";
import AuthContext from "connections/auth/AuthProvider.js";
import { successColor } from "assets/jss/material-dashboard-pro-react.js";
import { saveSetupkeyMapping } from "../../viewServices/apiRelationships.js";

const useStyles = makeStyles((theme) => ({
  headerBox: {
    backgroundColor: "#757575",
    borderBottom: "1px solid #e0e0e0",
    padding: theme.spacing(0.75, 1.5),
    marginBottom: 0,
    flexShrink: 0,
  },
  headerTitle: {
    fontSize: "0.8125rem",
    fontWeight: "500",
    color: "#ffffff",
  },
  scrollableContent: {
    flex: 1,
    overflowY: "auto",
    padding: theme.spacing(1.5),
    "&::-webkit-scrollbar": {
      width: "6px",
    },
    "&::-webkit-scrollbar-track": {
      background: "#f1f1f1",
      borderRadius: "3px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "#888",
      borderRadius: "3px",
      "&:hover": {
        background: "#555",
      },
    },
  },
  keyGroup: {
    marginBottom: theme.spacing(1.5),
    "&:not(:last-child)": {
      paddingBottom: theme.spacing(1.5),
    },
  },
  keyGroupTitle: {
    fontSize: "0.75rem",
    fontWeight: "500",
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1),
    letterSpacing: "0.3px",
  },
  divider: {
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
    backgroundColor: "#e0e0e0",
  },
  keyConfigBlock: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.75),
    marginBottom: theme.spacing(1.25),
    padding: theme.spacing(1.25, 1.5),
    border: "1px solid #e0e0e0",
    borderRadius: "6px",
    backgroundColor: "#ffffff",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    transition: "box-shadow 0.2s ease",
    "&:hover": {
      boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
    },
  },
  keyNameRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(0.5),
  },
  keyName: {
    fontSize: "0.8125rem",
    fontWeight: "500",
    color: theme.palette.secondary.main,
  },
  keyChipsContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.75),
    alignItems: "center",
  },
  keyChip: {
    fontSize: "0.75rem",
    height: "26px",
    backgroundColor: "#4caf50",
    color: "#ffffff",
    border: "none",
    "& .MuiChip-label": {
      padding: "0 10px",
      fontWeight: "500",
    },
    "& .MuiChip-deleteIcon": {
      color: "#ffffff",
      fontSize: "16px",
      marginRight: "4px",
      "&:hover": {
        color: "#e0e0e0",
      },
    },
  },
  columnDropdown: {
    minWidth: "120px",
    height: "26px",
    fontSize: "0.75rem",
    backgroundColor: "#e8f5e9",
    border: "1px dashed #4caf50",
    borderRadius: "16px",
    color: "#4caf50",
    fontWeight: "500",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "#c8e6c9",
      borderColor: "#388e3c",
    },
    "& .MuiSelect-select": {
      padding: "4px 12px",
      display: "flex",
      alignItems: "center",
      gap: "4px",
      "&:focus": {
        backgroundColor: "transparent",
        borderRadius: "16px",
      },
    },
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
    "& .MuiInput-underline:before": {
      borderBottom: "none",
    },
    "& .MuiInput-underline:after": {
      borderBottom: "none",
    },
    "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
      borderBottom: "none",
    },
    "& .MuiSelect-icon": {
      color: "#4caf50",
      right: "8px",
    },
    "&.Mui-focused": {
      backgroundColor: "#e8f5e9",
      boxShadow: "none",
    },
    "&.Mui-disabled": {
      backgroundColor: "#f5f5f5",
      borderColor: "#e0e0e0",
      color: "#9e9e9e",
      "& .MuiSelect-icon": {
        color: "#9e9e9e",
      },
    },
  },
  footerContainer: {
    padding: theme.spacing(1.5, 2),
    borderTop: "1px solid #e0e0e0",
    backgroundColor: "#fafafa",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    flexShrink: 0,
  },
  warningText: {
    fontSize: "0.8rem",
    color: "#999",
    fontWeight: "400",
    fontStyle: "italic",
  },
  actionButtons: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  revertButton: {
    minWidth: "80px",
    color: "#666",
    borderColor: "#ccc",
    fontSize: "0.8rem",
    padding: theme.spacing(0.5, 1.5),
    "&:hover": {
      backgroundColor: "#f5f5f5",
      borderColor: "#999",
    },
  },
  saveButton: {
    backgroundColor: (props) => props.clientColor,
    color: "#fff",
    minWidth: "140px",
    fontSize: "0.8rem",
    padding: theme.spacing(0.5, 2),
    position: "relative",
    "&:hover": {
      backgroundColor: successColor[1],
    },
  },
  saveBadge: {
    marginLeft: theme.spacing(1),
    "& .MuiBadge-badge": {
      backgroundColor: "#fff",
      color: (props) => props.clientColor,
      fontWeight: "600",
      fontSize: "0.75rem",
      minWidth: "15px",
      height: "15x",
      padding: "0 6px",
      position: "relative",
      transform: "none",
      right: "-4px",
    },
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    gap: theme.spacing(2),
  },
  emptyStateContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    padding: theme.spacing(3),
  },
  emptyStateText: {
    fontSize: "0.9rem",
    color: "#999",
    textAlign: "center",
  },
  disabledInsertMessage: {
    fontSize: "0.75rem",
    color: "#999",
    fontStyle: "italic",
    padding: theme.spacing(0.5, 1),
  },
}));

export default function SectionSetupKeys({
  selectedPremise,
  selectedTable,
  setupkeyMerges: availableSetupKeysByTable,
  getTableDescription,
  inputColumns,
  setupKeyMappings: setupKeyMappingsFromParent,
  isLoadingMappings,
  onMappingsChange,
  classes: parentClasses,
}) {
  const { userDetails } = useContext(AuthContext);
  const classes = useStyles({ clientColor: userDetails.implementation_color });

  // ===== STATE =====
  const [savedMappings, setSavedMappings] = useState({}); // Baseline (saved state)
  const [setupKeyMappings, setSetupKeyMappings] = useState({}); // Current state (editable)
  const [isSaving, setIsSaving] = useState(false);

  // ===== EFFECTS =====
  // Sync with parent's setupKeyMappings (already has UUID format)
  useEffect(() => {
    if (!setupKeyMappingsFromParent) {
      setSavedMappings({});
      setSetupKeyMappings({});
      return;
    }

    // Deep clone to avoid reference issues
    const clonedMappings = JSON.parse(
      JSON.stringify(setupKeyMappingsFromParent),
    );

    setSavedMappings(clonedMappings);
    setSetupKeyMappings(clonedMappings);
  }, [setupKeyMappingsFromParent]);

  // ===== COMPUTED VALUES =====
  const unsavedChanges = useMemo(() => {
    let count = 0;

    // Verifica todas as keys (saved + current)
    const allKeys = new Set([
      ...Object.keys(savedMappings),
      ...Object.keys(setupKeyMappings),
    ]);

    allKeys.forEach((compositeKey) => {
      const saved = savedMappings[compositeKey] || [];
      const current = setupKeyMappings[compositeKey] || [];

      // Extrai apenas os columnIds para comparação
      const savedColumnIds = saved.map((entry) => entry.columnId).sort();
      const currentColumnIds = current.map((entry) => entry.columnId).sort();

      // Compara arrays de columnIds
      const savedStr = JSON.stringify(savedColumnIds);
      const currentStr = JSON.stringify(currentColumnIds);

      if (savedStr !== currentStr) {
        count++;
      }
    });

    return count;
  }, [savedMappings, setupKeyMappings]);

  // ===== HANDLERS =====
  const handleAddColumn = (sourceTable, setupKey, columnId) => {
    const compositeKey = `${sourceTable}__${setupKey}`;
    const newEntry = {
      id: crypto.randomUUID(), // Gera ID único
      columnId: columnId,
    };
    setSetupKeyMappings((prev) => ({
      ...prev,
      [compositeKey]: [...(prev[compositeKey] || []), newEntry],
    }));
  };

  const handleRemoveColumn = (sourceTable, setupKey, entryId) => {
    const compositeKey = `${sourceTable}__${setupKey}`;
    setSetupKeyMappings((prev) => ({
      ...prev,
      [compositeKey]: (prev[compositeKey] || []).filter(
        (entry) => entry.id !== entryId, // Filtra por ID único
      ),
    }));
  };

  const handleSaveAll = async () => {
    try {
      if (unsavedChanges === 0) {
        toast.info("No changes to save", {
          position: "top-right",
          autoClose: 2000,
        });
        return;
      }

      // Identifica apenas as keys que mudaram
      const changedKeys = new Set();
      const allKeys = new Set([
        ...Object.keys(savedMappings),
        ...Object.keys(setupKeyMappings),
      ]);

      allKeys.forEach((compositeKey) => {
        const saved = savedMappings[compositeKey] || [];
        const current = setupKeyMappings[compositeKey] || [];

        // Extrai apenas os columnIds para comparação
        const savedColumnIds = saved.map((entry) => entry.columnId).sort();
        const currentColumnIds = current.map((entry) => entry.columnId).sort();

        const savedStr = JSON.stringify(savedColumnIds);
        const currentStr = JSON.stringify(currentColumnIds);

        if (savedStr !== currentStr) {
          changedKeys.add(compositeKey);
        }
      });

      // Transformar APENAS as keys alteradas para estrutura aninhada
      const mappingsBySourceTable = {};

      changedKeys.forEach((compositeKey) => {
        const [sourceTable, setupKey] = compositeKey.split("__");
        const entries = setupKeyMappings[compositeKey] || [];

        if (!mappingsBySourceTable[sourceTable]) {
          mappingsBySourceTable[sourceTable] = {};
        }

        // Extrai apenas os columnIds (remove os UUIDs) para enviar ao backend
        const columns = entries.map((entry) => entry.columnId);
        mappingsBySourceTable[sourceTable][setupKey] = columns;
      });

      // Inicia loading
      setIsSaving(true);

      // Salva apenas as mudanças no backend
      const response = await saveSetupkeyMapping(
        selectedPremise.premise_key,
        mappingsBySourceTable,
      );

      // Atualiza baseline (agora está tudo salvo)
      setSavedMappings({ ...setupKeyMappings });

      // Refresh mappings in parent to update status counters
      if (onMappingsChange) {
        onMappingsChange();
      }

      // Sucesso!
      toast.success(`Successfully saved ${response.created || 0} mapping(s)!`, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: false,
        theme: "light",
      });
    } catch (err) {
      console.error("❌ Failed to save:", err);
      toast.error("Failed to save mappings. Please try again.", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: false,
        theme: "light",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleRevertAll = () => {
    // Restaura para o estado salvo
    setSetupKeyMappings({ ...savedMappings });

    toast.info("All changes reverted", {
      position: "top-right",
      autoClose: 2000,
    });
  };

  const availableSetupKeysRaw = selectedTable
    ? availableSetupKeysByTable[selectedTable] || {}
    : {};

  // Só exibe colunas que são setup key de fato (is_setup_key no metadado)
  // e esconde o card da tabela-fonte que ficar sem nenhuma. Assim a
  // ocultação é dirigida pelo cadastro (ex.: key_region_used da chain03 e
  // key_client_used da chain04 marcadas is_setup_key=false), sem hardcode.
  const availableSetupKeys = Object.fromEntries(
    Object.entries(availableSetupKeysRaw)
      .map(([sourceTable, keys]) => {
        const cols = inputColumns[sourceTable]?.columns || {};
        return [
          sourceTable,
          keys.filter((k) => cols[k]?.is_setup_key === true),
        ];
      })
      .filter(([, keys]) => keys.length > 0),
  );

  return (
    <Box className={parentClasses.configColumn}>
      {/* HEADER - ALWAYS PRESENT */}
      <Box className={classes.headerBox}>
        <Typography className={classes.headerTitle}>Configure Keys</Typography>
      </Box>

      {/* SCROLLABLE CONTENT */}
      <Box className={classes.scrollableContent}>
        {/* EMPTY STATE - No table selected */}
        {!selectedTable ? (
          <Box className={classes.emptyStateContainer}>
            <Typography className={classes.emptyStateText}>
              Select a table to configure relationships
            </Typography>
          </Box>
        ) : /* LOADING STATE */
        isLoadingMappings ? (
          <Box className={classes.loadingContainer}>
            <ThreeDots
              color="#999"
              height={40}
              width={40}
              ariaLabel="loading"
            />
            <Typography className={classes.emptyStateText}>
              Loading configurations...
            </Typography>
          </Box>
        ) : /* NO KEYS AVAILABLE */
        Object.keys(availableSetupKeys).length === 0 ? (
          <Box className={classes.emptyStateContainer}>
            <Typography className={classes.emptyStateText}>
              No setup keys available for this table
            </Typography>
          </Box>
        ) : (
          /* CONTENT - Setup keys configuration */
          <>
            {Object.entries(availableSetupKeys).map(
              ([sourceTable, keys], groupIndex) => (
                <React.Fragment key={sourceTable}>
                  <Box className={classes.keyGroup}>
                    <Typography className={classes.keyGroupTitle}>
                      From table &quot;{getTableDescription(sourceTable)}&quot;
                    </Typography>

                    {keys.map((key) => {
                      const compositeKey = `${sourceTable}__${key}`;
                      const currentKeySelections =
                        setupKeyMappings[compositeKey] || [];
                      const columnsForSourceTable =
                        inputColumns[sourceTable]?.columns || {};

                      // Filtrar apenas is_setupkey_option (permite duplicatas!)
                      const availableOptions = Object.entries(
                        columnsForSourceTable,
                      )
                        .filter(([, col]) => col.is_setupkey_option === true)
                        .map(([colId, col]) => ({
                          id: colId,
                          name: col.column_name,
                        }));

                      // Buscar o column_name da setupKey
                      const keyName =
                        columnsForSourceTable[key]?.column_name || key;

                      // Verificar se a key permite configuração (column_source === "created")
                      const keyColumnSource =
                        columnsForSourceTable[key]?.column_source;
                      const isConfigurable = keyColumnSource === "created";

                      return (
                        <Box key={key} className={classes.keyConfigBlock}>
                          <Box className={classes.keyNameRow}>
                            <Typography className={classes.keyName}>
                              {keyName}:
                            </Typography>
                          </Box>

                          {isConfigurable ? (
                            <Box className={classes.keyChipsContainer}>
                              {/* Chips das colunas selecionadas */}
                              {currentKeySelections.map((entry) => {
                                const columnName =
                                  columnsForSourceTable[entry.columnId]
                                    ?.column_name || entry.columnId;

                                return (
                                  <Chip
                                    key={entry.id}
                                    label={columnName}
                                    size="small"
                                    className={classes.keyChip}
                                    onDelete={() =>
                                      handleRemoveColumn(
                                        sourceTable,
                                        key,
                                        entry.id,
                                      )
                                    }
                                    deleteIcon={<CloseIcon />}
                                  />
                                );
                              })}

                              {/* Dropdown para adicionar */}
                              <Select
                                value=""
                                onChange={(e) =>
                                  handleAddColumn(
                                    sourceTable,
                                    key,
                                    e.target.value,
                                  )
                                }
                                displayEmpty
                                size="small"
                                variant="standard"
                                disableUnderline
                                className={classes.columnDropdown}
                                disabled={availableOptions.length === 0}
                                renderValue={() => (
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    gap="4px"
                                  >
                                    <AddIcon style={{ fontSize: "16px" }} />
                                    <span>Add column</span>
                                  </Box>
                                )}
                              >
                                <MenuItem value="" disabled>
                                  Select a column to add
                                </MenuItem>
                                {availableOptions.map((opt) => (
                                  <MenuItem key={opt.id} value={opt.id}>
                                    {opt.name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </Box>
                          ) : (
                            <Typography
                              className={classes.disabledInsertMessage}
                            >
                              Relationship configuration for this key is
                              disabled
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                  </Box>

                  {/* DIVIDER BETWEEN GROUPS */}
                  {groupIndex < Object.keys(availableSetupKeys).length - 1 && (
                    <Divider className={classes.divider} />
                  )}
                </React.Fragment>
              ),
            )}
          </>
        )}
      </Box>

      {/* FOOTER - CHANGES STATUS AND ACTIONS */}
      {selectedTable &&
        !isLoadingMappings &&
        Object.keys(availableSetupKeys).length > 0 &&
        unsavedChanges > 0 && (
          <Box className={classes.footerContainer}>
            {/* Warning message */}
            <Typography className={classes.warningText}>
              {unsavedChanges} unsaved change{unsavedChanges > 1 ? "s" : ""}
            </Typography>

            {/* Action buttons */}
            <Box className={classes.actionButtons}>
              <Button
                variant="outlined"
                className={classes.revertButton}
                onClick={handleRevertAll}
                disabled={isSaving}
              >
                Revert
              </Button>
              <Button
                variant="contained"
                className={classes.saveButton}
                onClick={handleSaveAll}
                disabled={isSaving}
                startIcon={
                  isSaving ? (
                    <ThreeDots
                      color="#ffffff"
                      height={20}
                      width={20}
                      ariaLabel="saving"
                    />
                  ) : (
                    <SaveIcon />
                  )
                }
              >
                {isSaving ? "Saving..." : "Save All"}
                {!isSaving && (
                  <Badge
                    badgeContent={unsavedChanges}
                    className={classes.saveBadge}
                  />
                )}
              </Button>
            </Box>
          </Box>
        )}
    </Box>
  );
}

SectionSetupKeys.propTypes = {
  selectedPremise: PropTypes.object,
  selectedTable: PropTypes.string,
  setupkeyMerges: PropTypes.object.isRequired,
  getTableDescription: PropTypes.func.isRequired,
  inputColumns: PropTypes.object.isRequired,
  setupKeyMappings: PropTypes.object,
  isLoadingMappings: PropTypes.bool,
  onMappingsChange: PropTypes.func,
  classes: PropTypes.object.isRequired,
};
